import React, { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';
import { useBranding } from '../../contexts/BrandingContext';
import './PWAInstallPrompt.scss';

/**
 * Detect if the app is already running as an installed PWA.
 * Checks: display-mode standalone/fullscreen (Chrome, Edge, Firefox)
 *         navigator.standalone (iOS Safari)
 */
function isInstalledPWA() {
  if (window.navigator.standalone === true) return true;
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  if (window.matchMedia('(display-mode: fullscreen)').matches) return true;
  return false;
}

const PWAInstallPrompt = () => {
  const { brandName } = useBranding();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Don't show if already running as installed PWA
    if (isInstalledPWA()) return;
    // Don't show if user previously dismissed
    if (localStorage.getItem('pwa-install-dismissed')) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Also hide if app becomes installed while banner is visible
    const onInstalled = () => {
      setShow(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShow(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShow(false);
    setDeferredPrompt(null);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  if (!show) return null;

  return (
    <div className="pwa-install-banner">
      <div className="pwa-install-banner__content">
        <Download size={20} />
        <span>Install {brandName} for faster access</span>
      </div>
      <div className="pwa-install-banner__actions">
        <button className="pwa-install-banner__btn" onClick={handleInstall}>
          Install
        </button>
        <button
          className="pwa-install-banner__close"
          onClick={handleDismiss}
          aria-label="Dismiss"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
